/**
 * CardPreviewFrame — Card editor preview with iframe + timeout handling.
 *
 * Shows: loading spinner → iframe (on src ready) → timeout error (after 15s
 * if PREVIEW_READY PostMessage never arrives).
 *
 * Extracted from PageEditorPage.tsx to keep it under the 800-line limit.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

const TIMEOUT_MS = 15_000

interface CardPreviewFrameProps {
  /** Preview iframe URL (null while token is loading) */
  src: string | null
  /** True once the iframe sends PREVIEW_READY PostMessage */
  isReady: boolean
}

export function CardPreviewFrame({ src, isReady }: CardPreviewFrameProps) {
  const [timedOut, setTimedOut] = useState(false)
  const [iframeKey, setIframeKey] = useState(0)
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  // Start timeout when src is set; clear when iframe is ready
  useEffect(() => {
    clearTimeout(timerRef.current)
    setTimedOut(false)

    if (src && !isReady) {
      timerRef.current = setTimeout(() => setTimedOut(true), TIMEOUT_MS)
    }

    return () => clearTimeout(timerRef.current)
  }, [src, isReady, iframeKey])

  // Clear timeout as soon as iframe is ready
  useEffect(() => {
    if (isReady) {
      clearTimeout(timerRef.current)
      setTimedOut(false)
    }
  }, [isReady])

  const handleRetry = useCallback(() => {
    setTimedOut(false)
    setIframeKey((k) => k + 1)
  }, [])

  // No src yet — loading spinner
  if (!src) {
    return (
      <div className="relative flex h-full items-center justify-center bg-gray-100">
        <div className="flex flex-col items-center gap-3 text-gray-400">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-sky-500" />
          <span className="text-sm">Loading preview...</span>
        </div>
      </div>
    )
  }

  // Timeout — show error with retry
  if (timedOut) {
    return (
      <div className="relative flex h-full items-center justify-center bg-gray-100">
        <div className="flex flex-col items-center gap-3 text-center">
          <AlertTriangle className="h-8 w-8 text-amber-500" />
          <p className="text-sm font-medium text-gray-700">Preview could not be loaded</p>
          <p className="text-xs text-gray-400">Check your storefront connection and try again.</p>
          <button
            onClick={handleRetry}
            className="mt-2 flex items-center gap-1.5 rounded-sm border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Retry
          </button>
        </div>
      </div>
    )
  }

  // Normal — render iframe
  return (
    <div className="relative flex h-full items-center justify-center bg-gray-100">
      <iframe
        key={iframeKey}
        src={src}
        className="h-full w-full"
        sandbox="allow-scripts allow-same-origin"
        loading="eager"
      />
    </div>
  )
}
